import { useState, useEffect } from 'react';
import { useCourtCases } from '@/api/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { GlobeSpinner } from '@/components/ui/globe-spinner';
import { PageBanner } from '@/components/layout/PageBanner';
import { SortableHeader, type SortDir } from '@/components/ui/sortable-header';
import { TablePager } from '@/components/ui/table-pager';
import { Search, Scale, Loader2, X } from 'lucide-react';

const CASE_TYPE_VARIANT: Record<string, string> = {
  civil: 'info',
  criminal: 'destructive',
  bankruptcy: 'warning',
  family: 'success',
  appellate: 'default',
};

function caseTypeBadgeVariant(t: string | null): string {
  if (!t) return 'outline';
  return CASE_TYPE_VARIANT[t.toLowerCase()] ?? 'outline';
}

export function CourtCasesPage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [caseTypeFilter, setCaseTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('date_filed');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
      setPage(1);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, isFetching } = useCourtCases({
    q: debouncedSearch || undefined,
    state: stateFilter || undefined,
    case_type: caseTypeFilter || undefined,
    page,
    sort: sortField || undefined,
    order: sortField ? sortDir : undefined,
  });

  return (
    <div className="space-y-6 animate-fade-up p-1">
      <PageBanner>
        <div className="flex items-center justify-between">
          <div className="banner-chip">
            <h1 className="text-2xl font-bold banner-title">Court Cases</h1>
          </div>
          <div className="banner-chip">
            {isFetching && <Loader2 className="h-4 w-4 animate-spin" />}
            {data && <span className="text-sm text-muted-foreground">{data.total.toLocaleString()} cases</span>}
          </div>
        </div>

        <div className="flex gap-3 items-center flex-wrap">
          <div className="banner-chip flex-1 min-w-[200px]">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search case name, parties, case number..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 pr-9 h-11 text-base bg-background/90"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <div className="banner-chip">
            <label className="text-sm font-medium text-muted-foreground">State</label>
            <Input
              placeholder="ST"
              value={stateFilter}
              onChange={e => { setStateFilter(e.target.value.toUpperCase().slice(0, 2)); setPage(1); }}
              className="w-20 h-11 bg-background/90"
            />
          </div>
          <div className="banner-chip">
            <label className="text-sm font-medium text-muted-foreground">Type</label>
            <select
              value={caseTypeFilter}
              onChange={e => { setCaseTypeFilter(e.target.value); setPage(1); }}
              className="h-11 px-3 rounded-md border border-input bg-background/90 text-sm"
            >
              <option value="">All</option>
              <option value="civil">Civil</option>
              <option value="criminal">Criminal</option>
              <option value="bankruptcy">Bankruptcy</option>
              <option value="family">Family</option>
              <option value="appellate">Appellate</option>
              <option value="probate">Probate</option>
              <option value="small_claims">Small Claims</option>
            </select>
          </div>
        </div>
      </PageBanner>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Scale className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            <span className="text-amber-700 dark:text-amber-400">Case Records</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <GlobeSpinner label="Searching cases..." />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <SortableHeader label="Case #" field="case_number" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Case Name" field="case_name" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Type" field="case_type" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Filed" field="date_filed" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>Plaintiff</TableHead>
                    <TableHead>Defendant</TableHead>
                    <TableHead>Judge</TableHead>
                    <TableHead>
                      <SortableHeader label="Court" field="court_code" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Disposition" field="disposition" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.cases.map((c, idx) => (
                    <TableRow
                      key={c.id}
                      className={idx % 2 === 0 ? '' : 'bg-table-row-even'}
                    >
                      <TableCell className="font-mono text-xs">{c.case_number}</TableCell>
                      <TableCell className="max-w-[220px] truncate" title={c.case_name ?? ''}>
                        {c.source_url ? (
                          <a href={c.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 hover:underline">
                            {c.case_name || '-'}
                          </a>
                        ) : (
                          c.case_name || '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={caseTypeBadgeVariant(c.case_type) as any} className="text-xs">
                          {c.case_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {c.date_filed ? new Date(c.date_filed).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell className="max-w-[140px] truncate" title={c.plaintiff ?? ''}>{c.plaintiff || '-'}</TableCell>
                      <TableCell className="max-w-[140px] truncate" title={c.defendant ?? ''}>{c.defendant || '-'}</TableCell>
                      <TableCell className="max-w-[120px] truncate" title={c.judge ?? ''}>{c.judge || '-'}</TableCell>
                      <TableCell className="font-mono text-xs">{c.court_code}</TableCell>
                      <TableCell className="text-xs">{c.disposition || '-'}</TableCell>
                    </TableRow>
                  ))}
                  {data?.cases.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-16">
                        <div className="flex flex-col items-center gap-3">
                          <Scale className="h-10 w-10 text-muted-foreground/40" />
                          <div>
                            <p className="font-medium text-muted-foreground">No cases found</p>
                            <p className="text-sm text-muted-foreground/70 mt-1">Try adjusting your search terms or filters</p>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {(data?.cases.length ?? 0) > 0 && (
                <TablePager
                  page={page}
                  onPageChange={setPage}
                  total={data?.total}
                  pageSize={data?.limit ?? 20}
                  hasMore={data?.hasMore}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default CourtCasesPage;
